/**
 * @author      Shivani Mehra
 * @date        June, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Form, Row, Card, Container, Spinner } from 'react-bootstrap';
import { FaTrash, FaPlus, FaKeyboard, FaSave, FaSync } from 'react-icons/fa';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import DataApi from '../../../api/DataApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const WatconnectGreetingSettingPage = ({ selectedWhatsAppSetting }) => {
  const greetingApi = new DataApi('object/watconnect_greeting_setting');
  const greetingListApi = new DataApi('object/list/watconnect_greeting_setting');
  const whatsappSettingApi = new DataApi('whatsapp_setting');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessNumbers, setBusinessNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(selectedWhatsAppSetting || '');

  // Greeting Record State
  const [recordId, setRecordId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [messageType, setMessageType] = useState('text'); // 'text' | 'interactive_button' | 'interactive_list'
  const [body, setBody] = useState('Hi {{lead.name}}! Thank you for contacting us. How can we help you today?');
  const [headerType, setHeaderType] = useState('none'); // 'none' | 'text' | 'image' | 'video'
  const [headerText, setHeaderText] = useState('');

  // Interactive Button properties (will be stringified into button_list_config)
  const [buttons, setButtons] = useState([
    { id: '1744709651905', title: 'Start' }
  ]);

  // Interactive List properties (will be stringified into button_list_config)
  const [listButtonLabel, setListButtonLabel] = useState('View Details');
  const [listSections, setListSections] = useState([
    {
      title: 'Our Services',
      rows: [
        { id: 'row_1', title: 'Inquire Policy', description: 'Check details about your policy' },
        { id: 'row_2', title: 'Talk to Agent', description: 'Speak with a live representative' }
      ]
    }
  ]);

  // Object & Field dynamic states for variable binding helper
  const [objectList, setObjectList] = useState([]);
  const [selectedObject, setSelectedObject] = useState('');
  const [fieldList, setFieldList] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [loadingFields, setLoadingFields] = useState(false);

  // Attachment states
  const [uploadedFile, setUploadedFile] = useState(null); // { id, file_name, file_type }
  const [fileLoading, setFileLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);

  const textareaRef = useRef(null);

  // Load numbers and dynamic objects
  useEffect(() => {
    loadInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch configuration when selected number changes
  useEffect(() => {
    if (selectedNumber) {
      fetchGreetingConfig(selectedNumber);
    } else {
      resetForm();
      setLoading(false);
    }
  }, [selectedNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load files when recordId or headerType changes
  useEffect(() => {
    if (recordId && (headerType === 'image' || headerType === 'video')) {
      fetchUploadedFile(recordId);
    } else {
      setUploadedFile(null);
    }
  }, [recordId, headerType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Load available whatsapp settings
      const settingsRes = await whatsappSettingApi.fetchAll();
      if (settingsRes?.data?.success && settingsRes?.data?.record) {
        setBusinessNumbers(settingsRes.data.record);
        if (!selectedNumber && settingsRes.data.record.length > 0) {
          setSelectedNumber(settingsRes.data.record[0].phone);
        }
      }

      // Load Object configs for binding helper
      const objectsRes = await ObjectConfigApi.fetchObjectConfigs();
      if (objectsRes.success && objectsRes.data) {
        setObjectList(objectsRes.data.filter(obj => !obj.is_deleted));
      }
    } catch (err) {
      console.error('Failed to load initial settings data:', err);
      toast.error('Failed to load active settings.');
    }
  };

  const fetchGreetingConfig = async (phone) => {
    try {
      setLoading(true);
      // Fetch all records for watconnect_greeting_setting and filter by bunsiness_number
      const res = await greetingListApi.fetchAll();
      if (res?.data?.success && res?.data?.data) {
        // Find record matching bunsiness_number (exact match or sanitised number match)
        const match = res.data.data.find(r =>
          r.bunsiness_number === phone ||
          r.bunsiness_number?.replace(/\D/g, '') === phone?.replace(/\D/g, '')
        );

        if (match) {
          setRecordId(match.id);
          setIsActive(match.is_active ?? false);
          setMessageType(match.message_type ?? 'text');
          setBody(match.body ?? '');
          setHeaderType(match.header_type ?? 'none');
          setHeaderText(match.header_text ?? '');

          // Parse button_list_config
          if (match.button_list_config) {
            try {
              const parsed = JSON.parse(match.button_list_config);
              if (match.message_type === 'interactive_button') {
                setButtons(Array.isArray(parsed) ? parsed : []);
              } else if (match.message_type === 'interactive_list') {
                setListButtonLabel(parsed.button_label || 'View Details');
                setListSections(parsed.sections || []);
              }
            } catch (e) {
              console.warn('Failed to parse button_list_config:', e);
            }
          }
        } else {
          resetForm();
        }
      } else {
        resetForm();
      }
    } catch (err) {
      console.error('Error fetching greeting config:', err);
      toast.error('Error fetching greeting message configuration.');
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRecordId(null);
    setIsActive(false);
    setMessageType('text');
    setBody('Hi {{lead.name}}! Thank you for contacting us. How can we help you today?');
    setHeaderType('none');
    setHeaderText('');
    setButtons([{ id: '1744709651905', title: 'Start' }]);
    setListButtonLabel('View Details');
    setListSections([
      {
        title: 'Our Services',
        rows: [
          { id: 'row_1', title: 'Inquire Policy', description: 'Check details about your policy' },
          { id: 'row_2', title: 'Talk to Agent', description: 'Speak with a live representative' }
        ]
      }
    ]);
    setUploadedFile(null);
  };

  const fetchUploadedFile = async (parentId) => {
    try {
      setFileLoading(true);
      const attachmentsApi = new DataApi('attachments');
      const response = await attachmentsApi.fetchById(parentId);
      if (response?.data?.success && response.data.data?.length > 0) {
        // Use the latest attachment uploaded
        const latest = response.data.data[0];
        setUploadedFile(latest);
      } else {
        setUploadedFile(null);
      }
    } catch (err) {
      console.error('Error fetching greeting attachments:', err);
    } finally {
      setFileLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }

    if (headerType === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (headerType === 'video' && !file.type.startsWith('video/')) {
      toast.error('Please select a video file.');
      return;
    }

    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('description', `Greeting header ${headerType}`);

      const attachmentsApi = new DataApi('attachments');
      const res = await attachmentsApi.post(`/${recordId}`, formData);
      if (res?.data?.success && res.data.data?.length > 0) {
        toast.success('Header file uploaded successfully!');
        const uploaded = res.data.data[0];
        setUploadedFile(uploaded);
      } else {
        toast.error('Failed to upload file.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Error uploading file.');
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileDelete = async () => {
    if (!uploadedFile) return;
    if (!window.confirm('Are you sure you want to delete this header attachment?')) return;

    try {
      setFileUploading(true);
      const attachmentsApi = new DataApi('attachments');
      const res = await attachmentsApi.delete(uploadedFile.id);
      if (res?.data?.success) {
        toast.success('Header file deleted.');
        setUploadedFile(null);
      } else {
        toast.error('Failed to delete file.');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Error deleting file.');
    } finally {
      setFileUploading(false);
    }
  };

  const handleObjectChange = async (e) => {
    const objName = e.target.value;
    setSelectedObject(objName);
    setSelectedField('');
    setFieldList([]);

    if (!objName) return;

    try {
      setLoadingFields(true);
      const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objName);
      if (res.success && res.data?.fields) {
        setFieldList(res.data.fields.filter(f => !f.is_deleted));
      }
    } catch (err) {
      console.error('Failed to load fields for object:', objName, err);
      toast.error('Failed to load fields.');
    } finally {
      setLoadingFields(false);
    }
  };

  const insertField = () => {
    if (!selectedObject || !selectedField) {
      toast.warn('Please select both object and field to insert.');
      return;
    }

    const placeholder = `{{${selectedObject}.${selectedField}}}`;
    const textarea = textareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newValue = before + placeholder + after;
      setBody(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      setBody(prev => prev + placeholder);
    }
    toast.success(`Inserted ${placeholder}`);
  };

  // Manage Buttons
  const handleAddButton = () => {
    if (buttons.length >= 3) {
      toast.warn('WhatsApp buttons are limited to a maximum of 3.');
      return;
    }
    setButtons([...buttons, { id: String(Date.now()), title: `Button ${buttons.length + 1}` }]);
  };

  const handleRemoveButton = (index) => {
    setButtons(buttons.filter((_, idx) => idx !== index));
  };

  const handleButtonChange = (index, field, value) => {
    const newButtons = [...buttons];
    newButtons[index][field] = value;
    setButtons(newButtons);
  };

  // Manage List Sections & Rows
  const handleAddSection = () => {
    setListSections([...listSections, { title: 'New Section', rows: [{ id: String(Date.now()), title: 'Item Title', description: 'Item Description' }] }]);
  };

  const handleRemoveSection = (secIndex) => {
    setListSections(listSections.filter((_, idx) => idx !== secIndex));
  };

  const handleSectionTitleChange = (secIndex, title) => {
    const newSections = [...listSections];
    newSections[secIndex].title = title;
    setListSections(newSections);
  };

  const handleAddRow = (secIndex) => {
    const totalRows = listSections.reduce((sum, sec) => sum + sec.rows.length, 0);
    if (totalRows >= 10) {
      toast.warn('WhatsApp Interactive List allows a maximum of 10 rows in total.');
      return;
    }
    const newSections = [...listSections];
    newSections[secIndex].rows.push({
      id: String(Date.now()),
      title: 'New Item',
      description: 'New Description'
    });
    setListSections(newSections);
  };

  const handleRemoveRow = (secIndex, rowIndex) => {
    const newSections = [...listSections];
    newSections[secIndex].rows = newSections[secIndex].rows.filter((_, idx) => idx !== rowIndex);
    setListSections(newSections);
  };

  const handleRowChange = (secIndex, rowIndex, field, value) => {
    const newSections = [...listSections];
    newSections[secIndex].rows[rowIndex][field] = value;
    setListSections(newSections);
  };

  const handleSave = async () => {
    if (!selectedNumber) {
      toast.error('No business number selected.');
      return;
    }
    if (!body.trim()) {
      toast.error('Body text is required.');
      return;
    }

    // Interactive button validation
    if (messageType === 'interactive_button') {
      if (buttons.length === 0) {
        toast.error('At least one button is required for interactive button message.');
        return;
      }
      for (let btn of buttons) {
        if (!btn.title.trim()) {
          toast.error('Button titles cannot be empty.');
          return;
        }
        if (btn.title.length > 20) {
          toast.error(`Button title "${btn.title}" exceeds 20 characters limit.`);
          return;
        }
      }
      if (headerType === 'text' && !headerText.trim()) {
        toast.error('Header text is required when Text header is selected.');
        return;
      }
    }

    // Interactive list validation
    if (messageType === 'interactive_list') {
      if (!listButtonLabel.trim()) {
        toast.error('List button label is required.');
        return;
      }
      if (listSections.length === 0) {
        toast.error('At least one section is required for interactive lists.');
        return;
      }
      let totalRows = 0;
      for (let sec of listSections) {
        if (!sec.title.trim()) {
          toast.error('Section titles cannot be empty.');
          return;
        }
        if (sec.rows.length === 0) {
          toast.error(`Section "${sec.title}" must have at least one row.`);
          return;
        }
        for (let row of sec.rows) {
          totalRows++;
          if (!row.title.trim()) {
            toast.error('Row title is required.');
            return;
          }
          if (row.title.length > 24) {
            toast.error(`Row title "${row.title}" exceeds 24 characters limit.`);
            return;
          }
          if (row.description && row.description.length > 72) {
            toast.error(`Row description for "${row.title}" exceeds 72 characters limit.`);
            return;
          }
        }
      }
      if (totalRows > 10) {
        toast.error('A maximum of 10 rows total are allowed across all sections.');
        return;
      }
    }

    setSaving(true);

    try {
      // Structure button_list_config value
      let configValue = '';
      if (messageType === 'interactive_button') {
        configValue = JSON.stringify(buttons);
      } else if (messageType === 'interactive_list') {
        configValue = JSON.stringify({
          button_label: listButtonLabel,
          sections: listSections
        });
      }

      const payload = {
        name: `Greeting_${selectedNumber}`,
        bunsiness_number: selectedNumber,
        is_active: isActive,
        message_type: messageType,
        body: body,
        header_type: headerType,
        header_text: headerText,
        button_list_config: configValue
      };

      let result;
      if (recordId) {
        result = await greetingApi.update(payload, recordId);
      } else {
        result = await greetingApi.create(payload);
      }

      if (result?.data?.success) {
        toast.success('Greeting message configuration saved successfully!');
        if (result.data.data?.id) {
          setRecordId(result.data.data.id);
        }
      } else {
        toast.error(result?.data?.message || 'Failed to save configuration.');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('An error occurred while saving greeting message.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid className="px-5 py-4">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-dark mb-1">👋 Welcome Greeting Settings</h4>
          <p className="text-muted mb-0">Configure auto-reply messages sent to incoming leads/contacts.</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="light" size="sm" onClick={() => fetchGreetingConfig(selectedNumber)} disabled={loading}>
            <FaSync className={loading ? "spin" : ""} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading configuration...</p>
        </div>
      ) : (
        <Row>
          {/* Editor Card Column */}
          <Col lg={12} md={12} className="mb-4">
            <Card className="border-0 shadow-sm rounded-3 p-4">
              <Form>
                {/* Switch active and select number */}
                <Row className="mb-4 align-items-center">
                  <Col md={6}>
                    <Form.Group controlId="businessNumberSelect">
                      <Form.Label className="fw-semibold text-dark">Business Phone Number</Form.Label>
                      <Form.Select
                        value={selectedNumber}
                        onChange={(e) => setSelectedNumber(e.target.value)}
                        style={{ height: '42px', borderRadius: '6px' }}
                      >
                        <option value="">-- Select Number --</option>
                        {businessNumbers.map(n => (
                          <option key={n.id} value={n.phone}>
                            {n.name} ({n.phone})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
                    <div className="d-flex align-items-center gap-2 bg-light p-2 rounded-3 border">
                      <span className="small fw-semibold text-dark">Is Active</span>
                      <Form.Check
                        type="switch"
                        id="greeting-active-switch"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        style={{ transform: 'scale(1.1)' }}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Message Type Radio */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-dark d-block">Message Layout</Form.Label>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      id="radio-text"
                      label="Simple Text"
                      name="msgType"
                      checked={messageType === 'text'}
                      onChange={() => setMessageType('text')}
                    />
                    <Form.Check
                      type="radio"
                      id="radio-button"
                      label="Interactive Buttons"
                      name="msgType"
                      checked={messageType === 'interactive_button'}
                      onChange={() => setMessageType('interactive_button')}
                    />
                    <Form.Check
                      type="radio"
                      id="radio-list"
                      label="Interactive List Options"
                      name="msgType"
                      checked={messageType === 'interactive_list'}
                      onChange={() => setMessageType('interactive_list')}
                    />
                  </div>
                </Form.Group>

                {/* Field Binding Tool */}
                <Card className="bg-light border-0 shadow-none mb-4 rounded-3">
                  <Card.Body className="py-3 px-4">
                    <h6 className="fw-bold text-secondary mb-2 d-flex align-items-center">
                      <FaKeyboard className="me-2" /> Insert CRM Variables
                    </h6>
                    <Row className="g-2 align-items-end">
                      <Col sm={5}>
                        <Form.Label className="small fw-semibold text-muted">CRM Object</Form.Label>
                        <Form.Select size="sm" value={selectedObject} onChange={handleObjectChange} style={{ borderRadius: '6px' }}>
                          <option value="">-- Choose Object --</option>
                          {objectList.map(obj => (
                            <option key={obj.id} value={obj.object_name}>
                              {obj.object_label || obj.object_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col sm={5}>
                        <Form.Label className="small fw-semibold text-muted">Field Name</Form.Label>
                        <Form.Select
                          size="sm"
                          value={selectedField}
                          onChange={(e) => setSelectedField(e.target.value)}
                          disabled={!selectedObject || loadingFields}
                          style={{ borderRadius: '6px' }}
                        >
                          <option value="">
                            {loadingFields ? '-- Loading... --' : '-- Choose Field --'}
                          </option>
                          {fieldList.map(f => (
                            <option key={f.id} value={f.field_name}>
                              {f.field_label || f.field_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col sm={2}>
                        <Button
                          size="sm"
                          variant="primary"
                          className="w-100 fw-bold"
                          onClick={insertField}
                          disabled={!selectedObject || !selectedField}
                          style={{ borderRadius: '6px', height: '31px' }}
                        >
                          Insert
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Header Options (Button Type only) */}
                {messageType === 'interactive_button' && (
                  <Card className="border mb-4 rounded-3 p-3 bg-white">
                    <h6 className="fw-bold mb-3">Header Configuration</h6>
                    <Row className="mb-2 g-2">
                      <Col md={4}>
                        <Form.Label className="small fw-semibold">Header Type</Form.Label>
                        <Form.Select
                          size="sm"
                          value={headerType}
                          onChange={(e) => setHeaderType(e.target.value)}
                          style={{ borderRadius: '6px' }}
                        >
                          <option value="none">None</option>
                          <option value="text">Text Header</option>
                          <option value="image">Image Attachment</option>
                          <option value="video">Video Attachment</option>
                        </Form.Select>
                      </Col>

                      {headerType === 'text' && (
                        <Col md={8}>
                          <Form.Label className="small fw-semibold">Header Text</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="e.g. Welcome to Ibirds!"
                            value={headerText}
                            onChange={(e) => setHeaderText(e.target.value)}
                            style={{ borderRadius: '6px' }}
                          />
                        </Col>
                      )}

                      {(headerType === 'image' || headerType === 'video') && (
                        <Col md={8}>
                          {!recordId ? (
                            <div className="bg-light p-2 rounded border text-center small text-muted mt-2">
                              ⚠️ Save the greeting configuration first to enable media upload.
                            </div>
                          ) : fileLoading ? (
                            <div className="text-center py-2">
                              <Spinner animation="border" size="sm" /> Loading file details...
                            </div>
                          ) : uploadedFile ? (
                            <div className="bg-light p-2 rounded border mt-2 d-flex justify-content-between align-items-center">
                              <span className="small text-truncate fw-semibold me-2">
                                📎 {uploadedFile.file_name || 'Attached File'}
                              </span>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={handleFileDelete}
                                disabled={fileUploading}
                                style={{ borderRadius: '6px' }}
                              >
                                {fileUploading ? 'Deleting...' : <FaTrash />}
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <Form.Group controlId="headerFileUpload">
                                <Form.Label className="small fw-semibold text-muted">Upload Header File (Max 5MB)</Form.Label>
                                <div className="d-flex gap-2">
                                  <Form.Control
                                    type="file"
                                    size="sm"
                                    accept={headerType === 'image' ? 'image/*' : 'video/*'}
                                    onChange={handleFileUpload}
                                    disabled={fileUploading}
                                    style={{ borderRadius: '6px' }}
                                  />
                                  {fileUploading && (
                                    <div className="align-self-center">
                                      <Spinner animation="border" size="sm" />
                                    </div>
                                  )}
                                </div>
                              </Form.Group>
                            </div>
                          )}
                        </Col>
                      )}
                    </Row>
                  </Card>
                )}

                {/* Message Body */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-dark">Message Body Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    ref={textareaRef}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Type greeting message..."
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>

                {/* Interactive Buttons Config */}
                {messageType === 'interactive_button' && (
                  <Card className="border mb-4 rounded-3 p-3 bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Reply Buttons ({buttons.length}/3)</h6>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleAddButton}
                        disabled={buttons.length >= 3}
                        className="d-flex align-items-center gap-1"
                      >
                        <FaPlus size={10} /> Add Button
                      </Button>
                    </div>
                    {buttons.length === 0 ? (
                      <div className="text-center py-3 text-muted bg-light rounded-3">
                        No buttons configured.
                      </div>
                    ) : (
                      buttons.map((btn, index) => (
                        <Row key={btn.id} className="mb-2 align-items-end g-2">
                          <Col sm={5}>
                            <Form.Label className="small fw-semibold text-muted mb-1">Button Text (Max 20 chars)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={btn.title}
                              maxLength={20}
                              onChange={(e) => handleButtonChange(index, 'title', e.target.value)}
                              placeholder="e.g. Inquire Info"
                              style={{ borderRadius: '6px' }}
                            />
                          </Col>
                          <Col sm={5}>
                            <Form.Label className="small fw-semibold text-muted mb-1">Button ID (Unique Identifier)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={btn.id}
                              onChange={(e) => handleButtonChange(index, 'id', e.target.value)}
                              placeholder="e.g. btn_inquire"
                              style={{ borderRadius: '6px' }}
                            />
                          </Col>
                          <Col sm={2} className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="w-100"
                              onClick={() => handleRemoveButton(index)}
                              style={{ borderRadius: '6px', height: '31px' }}
                            >
                              <FaTrash size={12} />
                            </Button>
                          </Col>
                        </Row>
                      ))
                    )}
                  </Card>
                )}

                {/* Interactive List Config */}
                {messageType === 'interactive_list' && (
                  <Card className="border mb-4 rounded-3 p-3 bg-white">
                    <h6 className="fw-bold mb-3">Interactive List Options</h6>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Action Button Text (Max 20 chars)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        maxLength={20}
                        value={listButtonLabel}
                        onChange={(e) => setListButtonLabel(e.target.value)}
                        placeholder="e.g. Click to Select"
                        style={{ borderRadius: '6px' }}
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                      <h6 className="small fw-bold text-secondary mb-0">List Sections</h6>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleAddSection}
                        className="d-flex align-items-center gap-1"
                      >
                        <FaPlus size={10} /> Add Section
                      </Button>
                    </div>

                    {listSections.map((sec, secIndex) => (
                      <Card key={secIndex} className="bg-light border-0 shadow-none mb-3 p-3 rounded-3">
                        <Row className="mb-3 align-items-center g-2">
                          <Col sm={9}>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={sec.title}
                              placeholder="Section Name"
                              onChange={(e) => handleSectionTitleChange(secIndex, e.target.value)}
                              style={{ borderRadius: '6px', fontWeight: 'bold' }}
                            />
                          </Col>
                          <Col sm={3} className="text-end">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleAddRow(secIndex)}
                            >
                              + Row
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveSection(secIndex)}
                            >
                              <FaTrash size={10} />
                            </Button>
                          </Col>
                        </Row>

                        {sec.rows.map((row, rowIndex) => (
                          <Row key={row.id} className="bg-white border rounded-3 p-2 mb-2 g-2 align-items-end">
                            <Col sm={4}>
                              <Form.Label className="small text-muted mb-0" style={{ fontSize: '0.7rem' }}>Item Label (Max 24 chars)</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                maxLength={24}
                                value={row.title}
                                onChange={(e) => handleRowChange(secIndex, rowIndex, 'title', e.target.value)}
                                placeholder="Item label"
                                style={{ borderRadius: '6px' }}
                              />
                            </Col>
                            <Col sm={4}>
                              <Form.Label className="small text-muted mb-0" style={{ fontSize: '0.7rem' }}>Description (Max 72 chars)</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                maxLength={72}
                                value={row.description}
                                onChange={(e) => handleRowChange(secIndex, rowIndex, 'description', e.target.value)}
                                placeholder="Optional description"
                                style={{ borderRadius: '6px' }}
                              />
                            </Col>
                            <Col sm={3}>
                              <Form.Label className="small text-muted mb-0" style={{ fontSize: '0.7rem' }}>Item ID (Unique)</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                value={row.id}
                                onChange={(e) => handleRowChange(secIndex, rowIndex, 'id', e.target.value)}
                                placeholder="row_id"
                                style={{ borderRadius: '6px' }}
                              />
                            </Col>
                            <Col sm={1} className="text-center pb-1">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveRow(secIndex, rowIndex)}
                                style={{ borderRadius: '6px', padding: '2px 8px' }}
                              >
                                <FaTrash size={10} />
                              </Button>
                            </Col>
                          </Row>
                        ))}
                      </Card>
                    ))}
                  </Card>
                )}

                {/* Save Button */}
                <div className="d-flex justify-content-end mt-4 border-top pt-3">
                  <Button
                    variant="success"
                    className="fw-bold px-4 text-white"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ borderRadius: '6px', background: 'linear-gradient(135deg, #198754 0%, #157347 100%)' }}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" /> Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" /> Save Configuration
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>

        </Row>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
};

export default WatconnectGreetingSettingPage;
